import { type AxisSelection, FILE_JOB_MAX_FILES_PER_REQUEST } from '@atlas/types'
import { FilterRail, toggleFilterValue } from '@atlas/ui-components'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { useDeleteFile } from '@/hooks/useAssetDetail'
import { useAuth } from '@/hooks/useAuth'
import { useDiscoverFiles, useFacetedFilterRailAxes } from '@/hooks/useLibrary'
import { useThumbnails } from '@/hooks/useThumbnails'
import { canDeleteOwnedBy } from '@/lib/capabilities'
import {
	exitSelecting,
	startSelecting,
	toggle,
	toggleMany,
	useLibrarySelecting,
	useLibrarySelection,
} from '@/lib/library-selection-store'
import type { FileWithTagsDTO } from '@/services/file.service'
import { AssetGrid } from './AssetGrid'
import { LibraryHeader, type LibrarySelectionActions } from './LibraryHeader'
import { LibraryPager } from './LibraryPager'
import { RecentDownloads } from './RecentDownloads'
import { useLibraryZip } from './useLibraryZip'
import { ZipConfirmDialog } from './ZipConfirmDialog'

/** Axis groups shown by default; others are revealed via "Add variable". */
const DEFAULT_SHOWN_AXES = ['geography', 'brand', 'species']

/**
 * The header's ZIP selection surface, or nothing where there is nothing to pick.
 *
 * It is offered while the visible page holds files — and kept while the user is picking, so
 * a filter that narrows to nothing does not take the selection's own actions with it.
 *
 * `Select all` reads the CURRENT PAGE's ids only, which is what keeps it from ever reaching
 * the unloaded remainder of the filter, while the count reports the whole selection.
 */
function selectionActionsFor(
	pageFileIds: string[],
	selectedFileIds: ReadonlySet<string>,
	isSelecting: boolean,
	downloadZip: () => void,
): LibrarySelectionActions | undefined {
	if (pageFileIds.length === 0 && !isSelecting) return undefined
	const allOnPageSelected = pageFileIds.length > 0 && pageFileIds.every((id) => selectedFileIds.has(id))
	return {
		isSelecting,
		selectedCount: selectedFileIds.size,
		allOnPageSelected,
		someOnPageSelected: pageFileIds.some((id) => selectedFileIds.has(id)),
		overCap: selectedFileIds.size > FILE_JOB_MAX_FILES_PER_REQUEST,
		start: startSelecting,
		exit: exitSelecting,
		togglePage: () => toggleMany(pageFileIds, !allOnPageSelected),
		downloadZip,
	}
}

/**
 * Library — the asset-browse surface. A data-driven filter rail (per-axis
 * multi-selects) narrows a discovery-backed asset grid, one page at a time.
 * Upload & classify is one click away via the top-right button. Rail selection
 * accumulates OR-within / AND-across.
 *
 * Files can also be selected for a ZIP. Selecting is a MODE the header offers a
 * way into, and while it is on the cards are toggles rather than links. Both the
 * mode and the picked ids live in a module store rather than here, because they
 * belong to the whole Files experience and this page unmounts whenever a card is
 * opened.
 */
export function LibraryPage() {
	const navigate = useNavigate()
	const { user } = useAuth()
	const [selection, setSelection] = useState<AxisSelection>({})
	const [shownAxisCodes, setShownAxisCodes] = useState<string[]>(DEFAULT_SHOWN_AXES)
	const [page, setPage] = useState(1)
	const { axes } = useFacetedFilterRailAxes(selection, shownAxisCodes)
	const { files, isLoading, isFetching, hasNextPage, hasPreviousPage } = useDiscoverFiles(selection, page)
	const thumbnails = useThumbnails(files)
	const deleteFile = useDeleteFile()
	// The file awaiting confirmation, not a boolean: the dialog names it, and the grid is
	// optimistic — the row is gone before the request settles, so the name has to be held here.
	const [pendingDelete, setPendingDelete] = useState<FileWithTagsDTO | null>(null)

	const selectedFileIds = useLibrarySelection()
	const isSelecting = useLibrarySelecting()
	const zip = useLibraryZip(selectedFileIds)
	const selectionActions = selectionActionsFor(
		files.map((file) => file.fileId),
		selectedFileIds,
		isSelecting,
		zip.open,
	)

	const changeFilter = (axisCode: string, valueCode: string) => {
		setSelection((s) => toggleFilterValue(s, axisCode, valueCode))
		// A narrower filter has its own page 1; staying on page 4 of the old result
		// set would land on an empty grid. The FILE selection is deliberately untouched.
		setPage(1)
	}

	return (
		<div className="space-y-6">
			<LibraryHeader fileCount={files.length} onUpload={() => void navigate('/upload')} selection={selectionActions} />

			<RecentDownloads
				rows={zip.rows}
				isLoading={zip.isLoadingRows}
				selectedCounts={zip.selectedCounts}
				onDownload={zip.download}
			/>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_1fr]">
				<FilterRail
					axes={axes}
					shownAxisCodes={shownAxisCodes}
					selection={selection}
					onToggle={changeFilter}
					onAddAxis={(axisCode) => setShownAxisCodes((c) => (c.includes(axisCode) ? c : [...c, axisCode]))}
				/>
				<div className="space-y-6">
					<AssetGrid
						files={files}
						thumbnails={thumbnails}
						isLoading={isLoading}
						onOpenAsset={(fileId) => void navigate(`/assets/${fileId}`)}
						onDeleteAsset={setPendingDelete}
						selectionMode={isSelecting}
						selectedFileIds={selectedFileIds}
						onToggleSelect={toggle}
						canDeleteAsset={(f) => canDeleteOwnedBy(user?.role, user?.userId, f.uploadedBy)}
					/>
					<LibraryPager
						page={page}
						hasNextPage={hasNextPage}
						hasPreviousPage={hasPreviousPage}
						isFetching={isFetching}
						onPageChange={setPage}
					/>
				</div>
			</div>

			<ConfirmDialog
				open={pendingDelete !== null}
				title="Delete this asset?"
				description={
					pendingDelete
						? `${pendingDelete.fileName} and its classification will be removed. This cannot be undone.`
						: undefined
				}
				confirmLabel="Delete asset"
				confirmVariant="danger"
				busyLabel="Deleting…"
				submitting={deleteFile.isPending}
				onCancel={() => setPendingDelete(null)}
				onConfirm={() => {
					if (!pendingDelete) return
					// Closed as the request goes out, not after it lands: the removal is optimistic,
					// so the card is already gone and a dialog over an absent row reads as stuck.
					// Failure is reported by the mutation's own status-bar error, which survives this.
					deleteFile.mutate(pendingDelete.fileId)
					setPendingDelete(null)
				}}
			/>

			<ZipConfirmDialog
				open={zip.isConfirmOpen}
				selectedCount={selectedFileIds.size}
				submitting={zip.isSubmitting}
				errorMessage={zip.errorMessage}
				onCancel={zip.cancel}
				onConfirm={zip.confirm}
			/>
		</div>
	)
}
